/** Creator pools: the reads, the criteria summary and the small helpers the
 *  creators screens share. The message rules below are kept for the outreach
 *  flow (Phase B). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  api,
  type CreatorMemberFilter,
  type CreatorPlatform,
  type CreatorPoolItem,
  type OutreachAngle,
  type OutreachCounts,
  type OutreachFilter,
  type OutreachPreview,
  type OutreachRecipient,
  type OutreachSplit,
  type OutreachTone,
} from '@/api/client'
import { countGraphemes } from './graphemes'

/** Every creators read hangs under this key, so one invalidation refreshes
 *  the list, the pool, its people and the open drawer together. */
export const CK = {
  all: ['creators'] as const,
  pools: ['creators', 'pools'] as const,
  pool: (id: number) => ['creators', 'pool', id] as const,
  discovery: (id: number) => ['creators', 'discovery', id] as const,
  members: (id: number, f: CreatorMemberFilter) => ['creators', 'members', id, f] as const,
  person: (id: number) => ['creators', 'person', id] as const,
}

/** How often the pool page reads a running discovery again (tests shorten it). */
export const discoveryPoll = { ms: 2500 }

export function useCreatorPools() {
  return useQuery({ queryKey: CK.pools, queryFn: ({ signal }) => api.creatorPools(signal) })
}

export function useCreatorPool(id: number) {
  return useQuery({ queryKey: CK.pool(id), queryFn: ({ signal }) => api.creatorPool(id, signal), enabled: id > 0 })
}

/** The discovery card's state; reads again every couple of seconds while a run goes. */
export function usePoolDiscovery(id: number) {
  return useQuery({
    queryKey: CK.discovery(id),
    queryFn: ({ signal }) => api.poolDiscovery(id, signal),
    enabled: id > 0,
    refetchInterval: (q) => (q.state.data?.running ? discoveryPoll.ms : false),
  })
}

export function usePoolMembers(id: number, f: CreatorMemberFilter) {
  return useQuery({
    queryKey: CK.members(id, f),
    queryFn: ({ signal }) => api.poolMembers(id, f, signal),
    enabled: id > 0,
    placeholderData: (prev) => prev,
  })
}

export function useCreatorPerson(id: number) {
  return useQuery({ queryKey: CK.person(id), queryFn: ({ signal }) => api.creatorPerson(id, signal), enabled: id > 0 })
}

/** A write that changes pools or people; every creators read is read again. */
export function useCreatorMutation<V = void, R = unknown>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: CK.all }) })
}

/** "뷰티 · 팔로워 500+ · 최근 30일 활동 · 브랜드 제외": the criteria in one line. */
export function criteriaSummary(
  p: Pick<CreatorPoolItem, 'keywords' | 'followers_min' | 'followers_max' | 'active_days' | 'exclude_brands'>,
): string {
  const parts: string[] = []
  const kw = p.keywords ?? []
  parts.push(kw.length === 0 ? '키워드 없음' : kw.length > 3 ? `${kw.slice(0, 3).join(', ')} 외 ${kw.length - 3}` : kw.join(', '))
  const lo = p.followers_min ?? 0
  const hi = p.followers_max
  if (hi != null) parts.push(`팔로워 ${fmtCount(lo)}–${fmtCount(hi)}`)
  else if (lo > 0) parts.push(`팔로워 ${fmtCount(lo)}+`)
  if (p.active_days > 0) parts.push(`최근 ${p.active_days}일 활동`)
  if (p.exclude_brands) parts.push('브랜드 제외')
  return parts.join(' · ')
}

export const SOURCE_LABEL: Record<string, string> = {
  search: '검색',
  mention: '언급 따라가기',
  seed: '씨앗 계정',
  manual: '직접 추가',
  csv: 'CSV',
}

// ── Words ───────────────────────────────────────────────────────────────

export const LANGUAGES: { value: 'en' | 'ko' | 'ja'; label: string }[] = [
  { value: 'en', label: '영어' },
  { value: 'ko', label: '한국어' },
  { value: 'ja', label: '일본어' },
]

export const languageLabel = (l: string) => LANGUAGES.find((x) => x.value === l)?.label ?? l

export const CREATOR_PLATFORMS: CreatorPlatform[] = ['instagram', 'threads', 'bluesky']

/** The three fixed last lines — the same text as internal/creators/message.go. */
export const OPT_OUT_LINES: Record<string, string> = {
  en: "If it's not for you, no need to reply — I won't message again.",
  ko: '관심 없으시면 답장 안 하셔도 돼요. 다시 연락드리지 않을게요.',
  ja: 'ご興味がなければ返信は不要です。再度ご連絡することはありません。',
}

export const LINK_TOKEN = '{{link}}'
/** A DM may hold this many characters once the tracked link is filled in. */
export const MESSAGE_MAX = 1000
/** The tracked link counts as 40 characters (message.go linkPlaceholder). */
const LINK_LENGTH = 40

export interface MessageCheck {
  links: number
  linkOk: boolean
  optOutOk: boolean
  length: number
  lengthOk: boolean
  empty: boolean
  ok: boolean
}

/** The gate a message passes before approval, as the server applies it
 *  (creators.CheckBody): exactly one {{link}}, the opt-out line last, at most
 *  1000 characters with the link counted as 40. Bluesky counts graphemes. */
export function checkMessage(body: string, language: string, platform?: CreatorPlatform): MessageCheck {
  const text = body.trim()
  const links = text.split(LINK_TOKEN).length - 1
  const line = OPT_OUT_LINES[language] ?? ''
  const expanded = text.replace(LINK_TOKEN, 'x'.repeat(LINK_LENGTH))
  const length = platform === 'bluesky' ? countGraphemes(expanded) : Array.from(expanded).length
  const linkOk = links === 1
  const optOutOk = line !== '' && text.endsWith(line)
  const lengthOk = length <= MESSAGE_MAX
  const empty = text === ''
  return { links, linkOk, optOutOk, length, lengthOk, empty, ok: !empty && linkOk && optOutOk && lengthOk }
}

/** The opt-out line put at the end of a message (once). */
export function withOptOut(body: string, language: string): string {
  const line = OPT_OUT_LINES[language] ?? ''
  const text = body.trimEnd()
  if (!line || text.endsWith(line)) return body
  return text === '' ? line : `${text}\n\n${line}`
}

export const SKIP_REASON_LABEL: Record<string, string> = {
  error: '조회 오류',
  followers: '팔로워 조건',
  off_topic: '주제 아님',
  brand: '브랜드',
  inactive: '활동 없음',
  not_professional: '프로 계정 아님',
  not_found: '없는 계정',
  suppressed: '연락 금지',
  existing: '이미 있음',
  no_dm: 'DM 사절',
}

export const EVENT_LABEL: Record<string, string> = {
  draft_saved: '초안 저장',
  message_approved: '메시지 승인',
  message_discarded: '메시지 폐기',
  message_sent: '보냄',
  send_failed: '보내기 실패',
  link_clicked: '링크 클릭',
  state_changed: '상태 바뀜',
}

export const fmtCount = (n: number) => n.toLocaleString('ko-KR')
export const fmtRate = (r: number) => `${(r * 100).toFixed(1)}%`
export const hourText = (h: number) => `${String(h).padStart(2, '0')}:00`

// ── Outreaches (DM 보내기) ──────────────────────────────────────────────

export const OK = {
  list: ['creators', 'outreaches'] as const,
  one: (id: number) => ['creators', 'outreach', id] as const,
  recipients: (id: number, f: unknown) => ['creators', 'outreach', id, 'recipients', f] as const,
  preview: (body: unknown) => ['creators', 'outreach-preview', body] as const,
}

/** How often the outreach page reads again while drafts are being written (tests shorten it). */
export const generatePoll = { ms: 2500 }
/** How long the wizard waits after the last edit before it asks for a new preview. */
export const previewDebounce = { ms: 350 }

export function useOutreaches() {
  return useQuery({ queryKey: OK.list, queryFn: ({ signal }) => api.creatorOutreaches(signal) })
}

export function useOutreach(id: number) {
  return useQuery({
    queryKey: OK.one(id),
    queryFn: ({ signal }) => api.creatorOutreach(id, signal),
    enabled: id > 0,
    refetchInterval: (q) => (q.state.data?.generating?.running ? generatePoll.ms : false),
  })
}

export const SENT_STATES = ['sent', 'replied', 'accepted', 'declined'] as const

/** The people of one tab. The 보낸 것 tab is four states, read one by one and merged. */
export function useOutreachRecipients(id: number, tab: 'ready' | 'approved' | 'found' | 'sent', groupId: number, polling: boolean) {
  const f = { tab, groupId }
  return useQuery<{ items: OutreachRecipient[]; total: number }>({
    queryKey: OK.recipients(id, f),
    enabled: id > 0,
    placeholderData: (prev) => prev,
    refetchInterval: polling ? generatePoll.ms : false,
    queryFn: async ({ signal }): Promise<{ items: OutreachRecipient[]; total: number }> => {
      const g = groupId || undefined
      if (tab !== 'sent') return api.outreachRecipients(id, { groupId: g, state: tab, limit: 200 }, signal)
      const all = await Promise.all(SENT_STATES.map((s) => api.outreachRecipients(id, { groupId: g, state: s, limit: 200 }, signal)))
      const items = all.flatMap((r) => r.items)
      items.sort((a, b) => (b.message?.sent_at ?? '').localeCompare(a.message?.sent_at ?? ''))
      return { items, total: all.reduce((n, r) => n + r.total, 0) }
    },
  })
}

export function useOutreachPreview(body: { pool_id: number; filter: OutreachFilter; split: OutreachSplit } | null) {
  return useQuery({
    queryKey: OK.preview(body),
    queryFn: ({ signal }) => api.previewOutreach(body!, signal),
    enabled: body != null && body.pool_id > 0,
    placeholderData: (prev) => prev,
  })
}

export const ANGLES: { value: OutreachAngle; label: string }[] = [
  { value: 'review', label: '리뷰 부탁' },
  { value: 'feature', label: '기능 소개' },
  { value: 'collab', label: '협업 제안' },
]
export const TONES: { value: OutreachTone; label: string }[] = [
  { value: 'polite', label: '정중하게' },
  { value: 'casual', label: '가볍게' },
]

/** Sent so far = everyone who got the message, whatever happened after. */
export const sentOf = (c: OutreachCounts) => c.sent + c.replied + c.accepted + c.declined
export const repliedOf = (c: OutreachCounts) => c.replied + c.accepted + c.declined

export function sumCounts(list: OutreachCounts[]): OutreachCounts {
  const z: OutreachCounts = { found: 0, ready: 0, approved: 0, sent: 0, replied: 0, accepted: 0, declined: 0, skipped: 0, do_not_contact: 0 }
  for (const c of list) for (const k of Object.keys(z) as (keyof OutreachCounts)[]) z[k] += c[k] ?? 0
  return z
}

export const pct = (n: number, of: number) => (of > 0 ? `${((n / of) * 100).toFixed(1)}%` : '—')

export const STARTER: Record<string, (product: string) => string> = {
  ko: (p) => `안녕하세요 {{name}}님. 올리신 글을 보고 연락드렸어요. 저희가 만든 ${p || '앱'}을 써 보시고 솔직한 의견을 들려주시면 좋겠어요. 써 보실 수 있는 링크는 {{link}} 입니다.`,
  en: (p) => `Hi {{name}}, I came across your posts and wanted to reach out. I make ${p || 'an app'} and would love your honest take on it. You can try it here: {{link}}`,
  ja: (p) => `{{name}}さん、投稿を拝見してご連絡しました。私が作った${p || 'アプリ'}をお試しいただき、率直なご意見を伺えればうれしいです。こちらからお試しいただけます: {{link}}`,
}

export interface TemplateCheck {
  links: number
  length: number
  problems: string[]
  ok: boolean
}

/** The gate a group's template passes before the outreach is created: exactly
 *  one {{link}} (the server answers 422 otherwise), not empty, and short
 *  enough for the opt-out line the system adds at the end. */
export function checkTemplate(template: string, language: string, platform?: CreatorPlatform): TemplateCheck {
  const text = template.trim()
  const links = text.split(LINK_TOKEN).length - 1
  const line = OPT_OUT_LINES[language] ?? ''
  const full = `${text.replace(LINK_TOKEN, 'x'.repeat(LINK_LENGTH))}\n\n${line}`
  const length = platform === 'bluesky' ? countGraphemes(full) : Array.from(full).length
  const problems: string[] = []
  if (text === '') problems.push('템플릿을 적는다.')
  else if (links === 0) problems.push('{{link}}가 없다. 정확히 한 번 넣는다.')
  else if (links > 1) problems.push(`{{link}}가 ${links}번 있다. 정확히 한 번만 넣는다.`)
  if (length > MESSAGE_MAX) problems.push(`거절 안내까지 합쳐 ${MESSAGE_MAX}자를 넘는다 (지금 ${length}자).`)
  return { links, length, problems, ok: problems.length === 0 }
}

/** The template as a person would see it: {{name}} and {{link}} filled with
 *  examples and the system's opt-out line last. Used where no draft exists yet. */
export function fillTemplate(template: string, name: string, language: string): string {
  const line = OPT_OUT_LINES[language] ?? ''
  const body = template.trim().replace(/\{\{name\}\}/g, name).replace(LINK_TOKEN, 'https://…/i/abc123')
  return line ? `${body}\n\n${line}` : body
}

/** "연락할 수 있는 사람 39명 · 90일 안에 연락함 3명 · … 제외". */
export function previewSentence(p: OutreachPreview): string {
  const x = p.excluded
  const parts: string[] = []
  if (x.contacted_90d > 0) parts.push(`90일 안에 연락함 ${x.contacted_90d}명`)
  if (x.in_other_outreach > 0) parts.push(`다른 발송 진행 중 ${x.in_other_outreach}명`)
  if (x.suppressed > 0) parts.push(`연락 금지 ${x.suppressed}명`)
  if ((x.no_dm ?? 0) > 0) parts.push(`DM 사절 ${x.no_dm}명`)
  if (x.removed > 0) parts.push(`풀에서 뺌 ${x.removed}명`)
  if (x.filtered > 0) parts.push(`조건 밖 ${x.filtered}명`)
  const head = `연락할 수 있는 사람 ${fmtCount(p.eligible)}명`
  return parts.length ? `${head} · ${parts.join(' · ')} 제외` : head
}

/** "10:00" for 10 or "10:00". */
export function windowText(v: number | string): string {
  return typeof v === 'number' ? hourText(v) : v
}
